import { Stack } from "@mui/system";
import { useState } from "react";
import { useNestedDropdown } from "../../../../../hooks/useNestedDropdown";
import { DropdownNestedItem } from "../../../../../types/genereal";
import { DropdownSubItem } from "../../../../ui/dropdown/DropdownSubItem";
import { DropdownWithSearchWrapper } from "../../../../ui/dropdown/DropdownWithSearchWrapper";
import { ChipsList } from "./ChipsList";
import { DropdownParentItem } from "./DropdownParentItem";
import { Divider } from "@mui/material";
import { CustomChip } from "../../../../ui";

const ROW_LENGTH = 4;

type Props = {
  items: DropdownNestedItem[];
  selected: DropdownNestedItem[];
  setSelected: React.Dispatch<React.SetStateAction<DropdownNestedItem[]>>;
  placeholder: string;
  selectAllText?: string;
};

export const NestedDropdown = ({
  items,
  selected,
  setSelected,
  placeholder,
  selectAllText = "Select all",
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const {
    selectedCount,
    nestedItems,
    selectedParents,
    removeParent,
    toggleParent,
    toggleChild,
    removeChild,
    isChildSelected,
    checkParent,
    setInternalSelected,
    internalSelected,
  } = useNestedDropdown({
    items,
    selected,
    setSelected,
  });

  const cancelHandler = () => {
    setIsOpen(false);
    setSearch("");
    setInternalSelected(selected);
  };

  const submitHandler = () => {
    setIsOpen(false);
    setSearch("");
    setSelected(internalSelected);
  };

  return (
    <Stack gap="8px">
      <DropdownWithSearchWrapper
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        search={search}
        setSearch={setSearch}
        placeholder={placeholder}
        selectedCount={selectedCount}
        submitHandler={submitHandler}
        cancelHandler={cancelHandler}
        closeHandler={cancelHandler}
        fullwidth
        listMinHeight={"124px"}
      >
        {items
          .filter(
            (item) =>
              item.name.toLowerCase().includes(search.toLowerCase()) ||
              item.nestedItems.find((item) =>
                item.toLowerCase().includes(search.toLocaleLowerCase()),
              ),
          )
          .map((item) => {
            const { isSelected, nestedSelected } = checkParent(item.name);

            return (
              <DropdownParentItem
                initOpen={!!search}
                key={item.name}
                item={item}
                selectedChildCount={nestedSelected}
              >
                <DropdownSubItem
                  title={selectAllText}
                  checked={isSelected}
                  onClick={() => toggleParent(item)}
                />

                {item.nestedItems.length > 0 && (
                  <Divider style={{ margin: "0 16px" }} />
                )}

                {item.nestedItems
                  .filter((el) =>
                    el.toLowerCase().includes(search.toLowerCase()),
                  )
                  .map((sub) => (
                    <DropdownSubItem
                      key={sub}
                      title={sub}
                      disabled={isSelected}
                      checked={isChildSelected(sub)}
                      onClick={() => toggleChild(item.name, sub)}
                    />
                  ))}
              </DropdownParentItem>
            );
          })}
      </DropdownWithSearchWrapper>

      <ChipsList
        items={selectedParents}
        rowLength={ROW_LENGTH}
        renderItem={(item) => (
          <CustomChip
            key={item.name}
            label={
              item.nestedTotal > 0
                ? `${item.name} (${item.nestedTotal})`
                : item.name
            }
            onDelete={() => removeParent(item.name)}
          />
        )}
      />

      <ChipsList
        items={nestedItems}
        rowLength={ROW_LENGTH}
        buttonText={`+ ${nestedItems.length - ROW_LENGTH} more...`}
        renderItem={(item) => (
          <CustomChip
            key={item}
            label={item}
            onDelete={() => removeChild(item)}
          />
        )}
      />
    </Stack>
  );
};
